import React, { useState, useEffect } from 'react';
import { InputNumber, Button, Table } from 'antd';
import { useQuery, roundFloat, replaceFloat } from 'src/core/utils/formater';
import { useSelector, useDispatch } from 'react-redux';
import { start_loading, end_loading } from 'src/core/actions/loader';
import newAlert from 'src/components/newAlert';
import { set_alert } from 'src/core/actions/alert';
import debounce from 'lodash.debounce';

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editingValue, setEditingValue] = useState(null);

  const debouncedSave = debounce((value) => {
    const roundedValue = roundFloat(value);
    handleSave(record.key, dataIndex, roundedValue);
  }, 300);

  const handleChange = (value) => {
    setEditingValue(value);
    debouncedSave(value);
  };

  useEffect(
    () => () => {
      debouncedSave.cancel();
    },
    []
  );

  let childNode = children || '';

  // Add safety check for record and record.editable
  const isFieldEditable = record?.editable?.[dataIndex];

  if (isFieldEditable) {
    childNode = (
      <InputNumber
        min={0}
        value={editingValue !== null ? editingValue : record[dataIndex]}
        onChange={handleChange}
        className="wide-input-number"
        step={0.01}
        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
        parser={(value) => value?.replace(/\$\s?|(,*)/g, '')}
      />
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

const App = () => {
  const [dataSource, setDataSource] = useState([]);
  const { socket, data_date } = useSelector((state) => ({
    socket: state.socket,
    data_date: state.data_date,
  }));
  const query = useQuery();
  const dispatch = useDispatch();

  const handleSave = (key, dataIndex, value) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => key === item.key);
    const item = newData[index];

    // Round the value before saving it
    const roundedValue = replaceFloat(value);
    console.log('Original value:', value);
    console.log('Rounded value:', roundedValue);
    // Update the data source with the rounded value
    newData.splice(index, 1, { ...item, [dataIndex]: roundedValue });

    setDataSource(newData);
  };

  const handleSend = () => {
    // console.log(dataSource);
    // Add your save logic here
    socket.on('request_out', ({ status, content }) => {
      socket.removeAllListeners('request_out');
      newAlert({
        type: status === '0' ? 'success' : 'error',
        msg: content.Message,
        functions: { onSuccess: get_data(data_date.month) },
      });
      dispatch(end_loading({}));
    });
    const content = {
      year: data_date?.year,
      month: data_date?.month,
      action_type: 'save_tt11_tailan',
      table_data: dataSource,
    };
    socket.emit('request_in', {
      channel: 'hh01_modify',
      content,
    });
    dispatch(start_loading({}));
  };

  const get_data = (month) => {
    socket.on('request_out', ({ status, content }) => {
      socket.removeAllListeners('request_out');
      if (status === '0') {
        setDataSource(content?.tailan_data);
      } else {
        newAlert({ status: 'error', msg: 'Системд алдаа гарлаа' });
      }
      dispatch(end_loading({}));
    });

    socket.emit('request_in', {
      channel: 'hh01_view',
      content: {
        action_type: 'get_tt11_tailan',
        month,
        year: data_date?.year,
        search: {},
      },
    });

    dispatch(start_loading({}));
  };

  useEffect(() => {
    get_data(data_date.month);
  }, [data_date.month]);

  const columns = [
    {
      title: 'Үзүүлэлт',
      dataIndex: 'title',
      key: 'title',
      width: 600,
      align: 'left',
      render: (text, record) => {
        if (record && !record.mur) {
          return {
            children: (
              <strong style={{ textAlign: 'left', display: 'block' }}>
                {record.title}
              </strong>
            ),
            props: { colSpan: 4 },
          };
        }

        return {
          children: (
            <span
              style={{ textAlign: 'left', display: 'block' }}
              className={record.lvl2 ? 'mlvl2' : record.sub ? 'mlvl3' : ''}
            >
              {text}
            </span>
          ),
          props: {},
        };
      },
    },
    {
      title: 'МӨР',
      dataIndex: 'mur',
      key: 'mur',
      editable: false,
      width: 100,
      render: (text, record) => {
        if (record && !record.mur) {
          return { children: null, props: { colSpan: 0 } }; // hide group rows
        }
        return text;
      },
    },
    {
      title: 'ТАТВАР ТӨЛӨГЧИЙН ТОО',
      dataIndex: 'too',
      key: 'too',
      editable: true,
      width: 150,
      render: (text, record) => {
        if (record && !record.mur) {
          return { children: null, props: { colSpan: 0 } }; // hide group rows
        }
        return text;
      },
    },
    {
      title: 'ДҮН',
      dataIndex: 'dun',
      key: 'dun',
      editable: true,
      width: 150,
      render: (text, record) => {
        if (record && !record.mur) {
          return { children: null, props: { colSpan: 0 } }; // hide group rows
        }
        return roundFloat(text);
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) return col;
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: record.editable && record.editable[col.dataIndex], // Check if the field is editable
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  return (
    <div className="tt11-report-wrapper">
      {/* Title Section */}
      <div className="report-header">
        <div className="report-header-main">Монголын татварын алба</div>
        <div className="report-header-sub">
          Цалин, хөдөлмөрийн хөлс, шагнал, урамшуулал болон тэдгээртэй адилтгах
          хөдөлмөр эрхлэлтийн орлого, шууд бус орлогод ногдуулан суутгасан албан
          татварын тайлан
        </div>
        <div
          style={{ textAlign: 'right', marginBottom: '10px', fontSize: '14px' }}
        >
          Маягт ТТ-11
        </div>
      </div>

      <Table
        dataSource={dataSource}
        columns={mergedColumns}
        rowClassName={(record, index) =>
          record.title
            ? 'tt11-title-row'
            : index % 2 === 0
            ? 'tt11-even-row'
            : 'tt11-odd-row'
        }
        bordered
        pagination={false}
        components={{
          body: {
            cell: EditableCell,
          },
        }}
        // footer={() => (
        //   <Button
        //     type="primary"
        //     className="tt11-save-button"
        //     onClick={() => handleSend()}
        //   >
        //     Хадгалах
        //   </Button>
        // )}
      />
    </div>
  );
};

export default App;
